import type { ThemeOptions } from '@mui/material';
import { fontFamilies } from './tokens';

/**
 * Product typography: a comfortable reading size, sentence-case labels, and
 * headings that are confident without shouting. The brand wordmark is widely
 * letterspaced, and `overline` echoes that at small sizes — the only place the
 * spacing appears in the interface.
 *
 * One scale for every locale. Arabic gets two adjustments, both mechanical:
 * letter-spacing is zero (tracking pulls joined letters apart, and the
 * uppercase overline has no meaning in the script), and lines are a little
 * taller because Plex Arabic's ascenders and descenders reach further than
 * the Latin's.
 */
export function buildTypography(dir: 'ltr' | 'rtl'): ThemeOptions['typography'] {
  const rtl = dir === 'rtl';
  const fontFamily = rtl ? fontFamilies.arabic : fontFamilies.sans;
  const track = (value: string) => (rtl ? 0 : value);
  const lead = (value: number) => (rtl ? +(value + 0.1).toFixed(2) : value);
  return {
    fontFamily,
    fontSize: 14,
    htmlFontSize: 16,
    h1: { fontSize: '1.625rem', fontWeight: 600, letterSpacing: track('-0.02em'), lineHeight: lead(1.25) },
    h2: { fontSize: '1.375rem', fontWeight: 600, letterSpacing: track('-0.016em'), lineHeight: lead(1.3) },
    h3: { fontSize: '1.125rem', fontWeight: 600, letterSpacing: track('-0.012em'), lineHeight: lead(1.35) },
    h4: { fontSize: '1rem', fontWeight: 600, letterSpacing: track('-0.006em'), lineHeight: lead(1.4) },
    h5: { fontSize: '0.9375rem', fontWeight: 600, lineHeight: lead(1.45) },
    h6: { fontSize: '0.875rem', fontWeight: 600, lineHeight: lead(1.45) },
    subtitle1: { fontSize: '0.9375rem', fontWeight: 500, lineHeight: lead(1.5) },
    subtitle2: { fontSize: '0.8125rem', fontWeight: 600, lineHeight: lead(1.5) },
    body1: { fontSize: '0.9375rem', lineHeight: lead(1.6) },
    body2: { fontSize: '0.875rem', lineHeight: lead(1.55) },
    button: { fontSize: '0.875rem', fontWeight: 550, textTransform: 'none', letterSpacing: 0 },
    caption: { fontSize: '0.8125rem', lineHeight: lead(1.45) },
    /** Section labels — a quiet echo of the wordmark's spacing. */
    overline: {
      fontSize: '0.6875rem',
      fontWeight: 600,
      letterSpacing: track('0.12em'),
      textTransform: rtl ? 'none' : 'uppercase',
      lineHeight: lead(1.5),
    },
  };
}
